import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/catalog';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [paused]);

  const go = (next: number) => setIndex((next + heroSlides.length) % heroSlides.length);

  const handleTouchEnd = (clientX: number) => {
    if (touchStart.current === null) return;
    const delta = clientX - touchStart.current;
    if (Math.abs(delta) > 40) go(delta > 0 ? index - 1 : index + 1);
    touchStart.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => handleTouchEnd(e.changedTouches[0].clientX)}
      aria-label="最新推荐轮播"
    >
      <div className="relative h-[520px] w-full md:h-[600px] lg:h-[640px]">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 剧照`}
              title={`${slide.title} - 91大神推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-transparent to-foreground-950/40"></div>
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1400px] flex-col justify-end px-4 pb-16 md:px-8 md:pb-20 lg:pb-24">
          <div key={heroSlides[index].id} className="animate-fade-up max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-400/40 bg-accent-500/15 px-3 py-1 font-label text-xs font-medium text-accent-300">
              <i className="ri-flashlight-fill"></i>
              {heroSlides[index].kicker}
            </span>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl">
              {heroSlides[index].title}
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">{heroSlides[index].subtitle}</p>
            <p className="mt-4 font-label text-xs text-white/60 md:text-sm">{heroSlides[index].meta}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${heroSlides[index].id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${heroSlides[index].id === 'one-table-many-stories' ? 'show' : heroSlides[index].id === 'the-silent-code' ? 'tv' : 'movie'}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/30 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <i className="ri-layout-grid-line"></i>
                同类推荐
              </Link>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-2">
              {heroSlides.map((slide, i) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`切换到 ${slide.title}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index ? 'w-8 bg-accent-500' : 'w-4 bg-white/35 hover:bg-white/60'
                  }`}
                ></button>
              ))}
            </div>
            <span className="font-label text-xs text-white/50">
              0{index + 1} / 0{heroSlides.length}
            </span>
            <div className="ml-auto hidden items-center gap-2 md:flex">
              <button
                type="button"
                onClick={() => go(index - 1)}
                aria-label="上一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/15"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                type="button"
                onClick={() => go(index + 1)}
                aria-label="下一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/15"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}